import React from 'react';
import PropTypes from 'prop-types';
import {
  Text,
  Flex,
  Button,
  Container,
  Box,
  useTheme,
  chakra,
} from '@chakra-ui/react';

import Image from '@rsc/ui-components/tailwind/Image';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import Link from '@rsc/ui-components/tailwind/Link';
import Logo from '@rsc/ui-components/dept/atoms/Logo';

function CompanyCallout({ block }) {
  const theme = useTheme();

  return (
    <Box
      {...generateSectionId(block)}
      bgSize="cover"
      bgRepeat="no-repeat"
      backgroundImage={{
        base: 'none',
        lg: `${theme.images?.greyBackground}`,
      }}
      py={{
        base: 14,
        lg: 0,
        xl: '14rem',
      }}
      display="flex"
      flexDirection={{
        base: 'column',
        lg: 'row',
      }}
    >
      <Box
        bgSize="cover"
        bgColor="brand.ink"
        width={{
          base: '100%',
          lg: '50%',
        }}
      >
        <Container>
          <Flex
            pt={20}
            pb={{ base: 10, xl: 20 }}
            px={{ base: 0, xl: 20 }}
            textAlign={{ base: 'center', lg: 'left' }}
            flexDirection="column"
            alignItems={{ base: 'center', lg: 'flex-start' }}
          >
            <Logo
              h={{ base: '2.5rem', md: '4.34rem' }}
              w={{ base: '10.3rem', md: '17.7rem' }}
              yellow
            />
            <Text
              textStyle="h1"
              fontWeight="extrabold"
              color="typography.lighter"
              mt={10}
            >
              {block.headline}
              <chakra.span
                pos="absolute"
                top="auto"
                w="1px"
                h="1px"
                overflow="hidden"
                whiteSpace="nowrap"
              >
                {' '}
                unleash joy!
              </chakra.span>

              <chakra.span
                display="flex"
                flexDirection="column"
                alignItems={{ base: 'center', lg: 'flex-start' }}
              >
                <Image
                  alt="Unleash Joy"
                  className="mt-4 mb-10 hidden md:flex"
                  ratio={32 / 5}
                  src={theme.images?.unleashJoyWhite}
                />
                <Image
                  alt="Unleash Joy"
                  className="mt-4 mb-10 md:hidden"
                  ratio={32 / 5}
                  src={theme.images?.unleashJoyYellow}
                />
              </chakra.span>
            </Text>
            <Text textStyle="bodyLarge" color="typography.lighter">
              {block.copy}
            </Text>
            <Button as={Link} className="mt-8" link={block?.cta?.link}>
              {block.cta.text}
            </Button>
          </Flex>
        </Container>
      </Box>
      <Box
        position="relative"
        overflow={{ base: 'hidden', lg: 'visible' }}
        bgColor={{ base: 'brand.ink', lg: 'transparent' }}
        width={{ base: '100%', lg: '50%' }}
        h={{
          base: '28.8rem',
          sm: '31.8rem',
          md: '45.8rem',
          lg: 'auto',
        }}
        mt="-1px"
        sx={{
          div: {
            position: 'absolute',
          },
          img: {
            borderRadius: 'lg',
          },
          'div:nth-of-type(1)': {
            right: {
              base: '41%',
              md: '49%',
            },
            bottom: {
              base: '48%',
              sm: '50%',
              md: '51%',
              lg: '50%',
            },
            width: {
              base: '14.45rem',
              sm: '15.4375rem',
              md: '22rem',
              lg: '19rem',
              xl: '30.125rem',
            },
            zIndex: {
              base: 0,
              lg: 1,
            },
            clipPath: {
              base: 'path("M6.00822 225C2.61444 224.917 -0.0714264 222.119 0.00144958 218.741L0.00144958 68.8824C-0.0401917 64.4264 2.48952 60.3537 6.51831 58.4055L109.674 0.994843C112.516 -0.331619 115.806 -0.331619 118.648 0.994843L221.492 58.3951C225.51 60.3434 228.05 64.416 227.998 68.8721L227.998 218.192C228.082 221.87 225.156 224.917 221.471 225L6.00822 225Z")',
              sm: 'path("M6.50889 245C2.8323 244.91 -0.077392 241.87 0.00155312 238.201L0.00155312 75.4065C-0.0435584 70.5658 2.69696 66.1417 7.06149 64.0253L118.814 1.659C121.893 0.218042 125.457 0.218042 128.535 1.659L239.949 64.014C244.303 66.1304 247.055 70.5546 246.998 75.3953V237.604C247.088 241.6 243.919 244.91 239.927 245L6.50889 245Z")',
              md: 'path("M9.27585 349C4.03634 348.872 -0.110266 344.54 0.00223872 339.31L0.00223872 107.312C-0.0620497 100.414 3.84346 94.1087 10.0634 91.0927L169.322 2.21442C173.71 0.16092 178.788 0.16092 183.176 2.21442L341.952 91.0766C348.156 94.0927 352.078 100.398 351.997 107.296V338.46C352.126 344.155 347.61 348.872 341.92 349L9.27585 349Z")',
              lg: 'path("M8.01095 301C3.48592 300.889 -0.0952416 297.148 0.00192157 292.631V92.2696C-0.0536002 86.3118 3.31934 80.8666 8.69108 78.2618L146.232 1.50337C150.022 -0.270114 154.408 -0.270114 158.197 1.50337L295.322 78.248C300.68 80.8528 304.067 86.2979 303.998 92.2557L303.998 291.897C304.109 296.816 300.208 300.889 295.295 301L8.01095 301Z")',
              xl: 'path("M12.7016 477C5.52705 476.824 -0.150982 470.893 0.00307263 463.731L0.00307263 146.052C-0.0849586 136.606 5.26293 127.973 13.78 123.843L231.855 2.14022C237.864 -0.67168 244.818 -0.67168 250.826 2.14022L468.241 123.821C476.736 127.951 482.106 136.584 481.996 146.03L481.996 462.567C482.172 470.366 475.988 476.824 468.197 477L12.7016 477Z")',
            },
          },
          'div:nth-of-type(2)': {
            left: {
              base: '52%',
              lg: '46%',
            },
            top: {
              base: '39%',
              md: '37%',
              lg: '30%',
            },
            zIndex: {
              base: 1,
              lg: 0,
            },
          },
          'div:nth-of-type(3)': {
            right: {
              base: '53%',
              sm: '52%',
              lg: '57%',
            },
            top: {
              base: '57%',
              sm: '54%',
              lg: '52%',
              xl: '55%',
            },
          },
        }}
      >
        {block.images?.map(item => (
          <Image
            className="rounded-lg w-[12.45rem] sm:w-56 md:w-80 lg:w-[17rem] xl:w-[26rem]"
            key={item._key}
            sanityImage={item.image}
          />
        ))}
      </Box>
    </Box>
  );
}

CompanyCallout.propTypes = {
  block: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    cta: PropTypes.shape({
      link: PropTypes.shape({
        href: PropTypes.string,
        linkType: PropTypes.string,
        internalLink: PropTypes.shape({}),
      }),
      text: PropTypes.string.isRequired,
    }),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string.isRequired,
          }),
          alt: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
};

export default CompanyCallout;
